import gql from 'graphql-tag';

import i18next from '../i18n/config';
import Snackbar from './snackbar';
import StringUtil from '../util/StringUtil';
import { GoogleAnalyticsConnectorCoreAttributes } from './connectors';
import { CustomCheckCoreAttributesWithoutSites } from './customChecks';
import { ErrorsAttributes } from '../constants';

export const SiteCoreAttributes = `
id
name
url
enabled
online
crawlStartedAt
updatedAt
nextCrawlTime
pagesCount
imagesCount
pdfsCount
statusMessage
state
crawlerConfiguration {
  method
  pdfCheck
  crawlerVersion
  passwordProtected
}
bypassUrlRedirectCheck
a11yCheck
bestPracticesCheck
linkCheck
profanityCheck
seoCheck
spellCheck
webGovernanceCheck
`;

export const SiteQueryAttributes = `
  ${SiteCoreAttributes}
  daysBetweenCrawls
  keepDuplicatePages
  crawlerConfiguration {
    googleAnalyticsConnectorId
    crawlDepth
    pageLimit
    method
    obeyRobotsTxt
    protocolAgnostic
    onloadClickSelectors
    removeMatchingElements
    pagesCount
    urlList
    timeout
    delay
    processingDelay
    ignoreUrlQueryPages
    userAgentOverride
    passwordProtected
    protectedSiteType
    username
    password
    loginPageUrl
    usernameFieldSelector
    passwordFieldSelector
    formSubmitSelector
    authScriptSteps {
      action
      args
      namedArgs
    }
    pdfCheck
    disableProxy
    scrollPage
    scrollPageSelector
    scheduleCrawl
    crawlDay
    crawlHour
    injectedJs
    viewportHeight
    viewportWidth
    viewportIsLandscape
    viewportDeviceScaleFactor
    viewportIsMobile
    viewportHasTouch
    additionalDomains
    crawlerVersion
    wwwAgnostic
    waitUntilElementType
    waitUntilElementSelector
    reduceMotion
  }
  longWordsSplit
  dictionaries
  ignoredLinks
  ignoredLinkReasons
  ignoredPaths
  ignoredPathRegexes
  ignoredUrlRegexes
  ignoredProfaneWords
  ignoredWords
  ignoreCapitalizedWords
  ignoredAccessibility {
    id
    issueId
    help
    elementSource
  }
  ignoredBestPractices {
    id
    issueId
    help
    elementSource
  }
  ignoredSeoIssues {
    id
    help
  }
  a11yCheck
  bestPracticesCheck
  linkCheck
  profanityCheck
  seoCheck
  spellCheck
  webGovernanceCheck
  customChecks {
    ${CustomCheckCoreAttributesWithoutSites}
  }
  account {
    id
    name
    ignoredLinkReasons
    customChecks {
      ${CustomCheckCoreAttributesWithoutSites}
    }
    dictionariesLimit
    dictionaries
    longWordsSplit
  }
  groups {
    id
    name
    allSites
    includesAllSitesPages
    pageSetRulesAggregator
  }
  userMemberships {
    id
    role
    user {
      id
      name
    }
  }
  googleAnalyticsConnectorId
`

export const SiteQuery = gql`
  query SiteQuery($siteId: String!, $accountId: String!) {
    site(siteId: $siteId, accountId: $accountId) {
      ${SiteQueryAttributes}
    }
    connectors(accountId: $accountId, sortBy: name, sortOrder: asc) {
      nodes {
        ...on GoogleAnalyticsConnector {
          ${GoogleAnalyticsConnectorCoreAttributes}
        }
      }
    }
  }
`;

export const SiteQueryForRouting = gql`
  query SiteQueryForRouting($siteId: String!, $accountId: String!) {
    site(siteId: $siteId, accountId: $accountId) {
      ${SiteCoreAttributes}
    }
  }
`;

export const SiteStateQuery = gql`
  query SiteStateQuery($siteId: String!, $accountId: String!) {
    site(siteId: $siteId, accountId: $accountId) {
      id
      state
    }
  }
`;

export const SiteUptimeStatsQuery = gql`
  query SiteUptimeStatsQuery($siteId: String!, $accountId: String!) {
    site(siteId: $siteId, accountId: $accountId) {
      id
      name
      url
      uptimeMonitoring {
        url
        paused
        lastResult {
          time
          status
          statusText
          isOk
        }
        lastStatusChange {
          time
          status
          statusText
          isOk
        }
        smsNotificationPhoneNumbers
        emailNotificationAddresses
        webhookNotificationUrls
      }
    }
    uptimeStats(siteId: $siteId, accountId: $accountId) {
      rate1
      rate1Change
      rate7
      rate7Change
      rate30
      rate30Change
    }
  }
`;

export const SiteUptimeChecksQuery = gql`
  query SiteUptimeChecksQuery($siteId: String!, $accountId: String!, $page: Int!, $perPage: Int!, $sortBy: UptimeChecksSortBy!, $sortOrder: SortOrder!, $search: String, $filter: CheckTypesFilter!) {
    uptimeChecks(siteId: $siteId, accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, filter: $filter) {
      totalEntries
      nodes {
        time
        duration
        status
        statusText
      }
    }
  }
`;

export const SiteDashboardSnapshotFields = `
  id
  score
  trend
  updatedAt
  accessibility {
    score
    total
    trend
    affectedPagesCount
  }
  badLinks {
    score
    total
    trend
    affectedPagesCount
  }
  spelling {
    score
    total
    trend
    affectedPagesCount
  }
  profanity {
    score
    total
    trend
    affectedPagesCount
  }
  webGovernance {
    score
    total
    trend
    affectedPagesCount
  }
  seo {
    score
    total
    trend
    affectedPagesCount
  }
  bestPractices {
    score
    total
    trend
    affectedPagesCount
  }
`

// accessibilityCount is included explicitly in this query so
// it can override the latestStatsSnapshot for content editors
export const SiteDashboardQuery = gql`
  query SiteDashboardQuery($siteId: String!, $accountId: String!) {
    site(siteId: $siteId, accountId: $accountId) {
      ${SiteCoreAttributes}
      accessibilityCount
      webGovernanceCount
      badLinksCount
      misspellingsCount
      seoIssuesCount
      bestPracticesCount
      latestStatsSnapshot {
        ${SiteDashboardSnapshotFields}
      }
      latestCrawl {
        id
        createdAt
        status
        pagesCount
        finishType
      }
      uptimeMonitoring {
        enabled
        lastResult {
          duration
          time
          status
          statusText
          isOk
        }
      }
      reportTemplates {
        id
        name
        latestReport(siteId: $siteId) {
          id
          date
          url
        }
      }
      account {
        id
        delinquent
        delinquentReason
      }
    }
  }
`;

export const SiteDashboardAnalyticsQuery = gql`
  query SiteDashboardAnalyticsQuery($siteId: String, $accountId: String!, $dateRange: SiteAnalyticsDateRange!) {
    analytics(accountId: $accountId, siteId: $siteId, dateRange: $dateRange) {
      #totalViews
      connectorAvailable
    }
  }
`;

export const GroupDashboardAnalyticsQuery = gql`
  query GroupDashboardAnalyticsQuery($groupId: String, $accountId: String!, $dateRange: SiteAnalyticsDateRange!) {
    analytics(accountId: $accountId, groupId: $groupId, dateRange: $dateRange) {
      #totalViews
      connectorAvailable
    }
  }
`;

export const SitesQuery = gql`
  query SitesQuery($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: SitesSortBy!, $sortOrder: SortOrder!, $search: String, $pinnedOnly: Boolean) {
    sites(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, pinnedOnly: $pinnedOnly) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        ${SiteCoreAttributes}
        pinned
      }
    }
  }
`;

export const SitesAttributesForRouting = `
  id
  name
  url
  enabled
  statusMessage
  state
  pinned
  crawlerConfiguration {
    method
  }
`;

export const SitesQueryForRouting = gql`
  query SitesQueryForRouting($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: SitesSortBy!, $sortOrder: SortOrder!, $search: String, $pinnedOnly: Boolean) {
    sites(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, pinnedOnly: $pinnedOnly) {
      currentPage
      totalPages
      nodes {
        ${SitesAttributesForRouting}
      }
    }
  }
`;

export const SitesQueryForSelection = gql`
  query SitesQueryForSelection($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: SitesSortBy!, $sortOrder: SortOrder!, $search: String, $groupId: String, $pinnedOnly: Boolean) {
    sites(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, groupId: $groupId, pinnedOnly: $pinnedOnly) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        ${SitesAttributesForRouting}
      }
    }
  }
`;

export const SitesMembershipsQuery = gql`
  query SitesMembershipsQuery($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: SitesSortBy!, $sortOrder: SortOrder!) {
    sites(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        id
        name
        url
        crawlerConfiguration {
          method
        }
        userMemberships {
          role
          user {
            id
            name
            email
            lastSignInAt
            confirmedAt
          }
        }
      }
    }
  }
`;

export const SitesUptimeQuery = gql`
  query SitesUptimeQuery($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: SitesSortBy!, $sortOrder: SortOrder!, $search: String, $uptimeMonitoringEnabled: Boolean) {
    sites(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, uptimeMonitoringEnabled: $uptimeMonitoringEnabled) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        id
        name
        url
        uptimeMonitoring {
          url
          paused
          lastResult {
            time
            status
            statusText
            isOk
          }
          lastStatusChange {
            time
            status
            statusText
            isOk
          }
        }
      }
    }
  }
`;

const SiteCrawlFragment = `
  fragment SiteCrawlFragment on Crawl {
    id
    createdAt
    updatedAt
    startedAt
    finishedAt
    status
    finishType
    retryCount
    counts {
      assetsCount
      crawledCount
      crawlingCount
      errorCount
      ignoredCount
      pagesCount
      processingCount
      refreshedAt
      skippedCount
      totalCount
    }
    previousCrawl {
      id
    }
    nextCrawl {
      id
    }
  }
`;

const PageInfoFragment = `
  fragment PageInfoFragment on PageInfo {
    endCursor
    hasNextPage
    hasPreviousPage
    startCursor
  }
`;

export const SiteCrawlsQuery = gql`
  query SiteCrawlsQuery($siteId: String!, $accountId: String!, $after: String, $first: Int, $before: String, $last: Int, $sortBy: CrawlSortBy!, $sortOrder: SortOrder!) {
    site(siteId: $siteId, accountId: $accountId) {
      crawlCount
      crawls(after: $after, first: $first, before: $before, last: $last, sortBy: $sortBy, sortOrder: $sortOrder) {
        nodes {
          ...SiteCrawlFragment
        }
        pageInfo {
          ...PageInfoFragment
        }
      }
    }
  }
  ${SiteCrawlFragment}
  ${PageInfoFragment}
`;

export const SiteCrawlQuery = gql`
  query SiteCrawlQuery($siteId: String!, $accountId: String!, $crawlId: ID!) {
    site(siteId: $siteId, accountId: $accountId) {
      id
      name
      crawlerConfiguration {
        crawlerVersion
      }
      latestCrawl {
        id
      }
      crawl(id: $crawlId) {
        ...SiteCrawlFragment
      }
    }
  }
  ${SiteCrawlFragment}
`;

export const RefreshCrawlCountsMutation = gql`
  mutation RefreshCrawlCounts($siteId: String!, $accountId: String!, $crawlId: String!) {
    refreshCrawlCounts(siteId: $siteId, accountId: $accountId, crawlId: $crawlId) {
      site {
        id
        name
        latestCrawl {
          id
        }
      }
      crawl {
        ...SiteCrawlFragment
      }
    }
  }
  ${SiteCrawlFragment}
`;

export const SiteCrawlUrlsQuery = gql`
  query SiteCrawlUrlsQuery($siteId: String!, $accountId: String!, $crawlId: ID!, $first: Int, $last: Int, $after: String, $before: String, $status: String, $type: String, $search: String) {
    site(siteId: $siteId, accountId: $accountId) {
      id
      name
      latestCrawl {
        id
      }
      crawl(id: $crawlId) {
        id
        finishType
        crawlUrlsCount(status: $status, type: $type, search: $search)
        crawlUrls(first: $first, last: $last, after: $after, before: $before, status: $status, type: $type, search: $search) {
          pageInfo {
            ...PageInfoFragment
          }
          nodes {
            id
            url
            status
            httpStatus
            createdAt
            updatedAt
            message
            page {
              id
              path
              title
            }
            asset {
              id
              path
              contentType
            }
          }
        }
      }
    }
  }
  ${PageInfoFragment}
`;

export const CreateSiteDependenciesQuery = gql`
  query CreateSiteDependenciesQuery($accountId: String!) {
    customChecks(accountId: $accountId, page: 1, perPage: 100, sortBy: name, sortOrder: asc, includeShared: true) {
      nodes {
        ${CustomCheckCoreAttributesWithoutSites}
      }
    }
    connectors(accountId: $accountId, sortBy: name, sortOrder: asc) {
      nodes {
        ...on GoogleAnalyticsConnector {
          ${GoogleAnalyticsConnectorCoreAttributes}
        }
      }
    }
    groups(accountId: $accountId, page: 1, perPage: 100, sortBy: name, sortOrder: asc) {
      nodes {
        id
        name
        allSites
      }
    }
    account(accountId: $accountId) {
      id
      name
      ignoredLinkReasons
      dictionariesLimit
      dictionaries
      longWordsSplit
    }
  }
`;

export const CopySiteDependenciesQuery = gql`
  query CopySiteDependenciesQuery($accountId: String!, $siteId: String!) {
    customChecks(accountId: $accountId, page: 1, perPage: 100, sortBy: name, sortOrder: asc, includeShared: true) {
      nodes {
        ${CustomCheckCoreAttributesWithoutSites}
      }
    }
    connectors(accountId: $accountId, sortBy: name, sortOrder: asc) {
      nodes {
        ...on GoogleAnalyticsConnector {
          ${GoogleAnalyticsConnectorCoreAttributes}
        }
      }
    }
    groups(accountId: $accountId, page: 1, perPage: 100, sortBy: name, sortOrder: asc) {
      nodes {
        id
        name
        allSites
      }
    }
    account(accountId: $accountId) {
      id
      name
      ignoredLinkReasons
      longWordsSplit
    }
    site(siteId: $siteId, accountId: $accountId) {
      ${SiteQueryAttributes}
    }
  }
`;

export const CreateSiteMutation = gql`
  mutation CreateSite($accountId: String!, $name: String, $url: String, $enabled: Boolean, $a11yCheck: Boolean, $bestPracticesCheck: Boolean, $spellCheck: Boolean, $profanityCheck: Boolean, $linkCheck: Boolean, $seoCheck: Boolean, $webGovernanceCheck: Boolean, $daysBetweenCrawls: Int, $customCheckIds: [String!], $ignoredPaths: [String!], $ignoredPathRegexes: [String!], $ignoredUrlRegexes: [String!], $dictionaries: [SiteDictionary!], $longWordsSplit: Int, $ignoredAccessibility: [IgnoreIssuesInput!], $ignoredBestPractices: [IgnoreIssuesInput!], $ignoredLinks: [String!], $ignoredWords: [String!], $ignoreCapitalizedWords: Boolean, $ignoredProfaneWords: [String!], $ignoredLinkReasons: [String!], $ignoredSeoIssues: [String!], $crawlerConfiguration: CrawlerConfigurationInput, $nextCrawlTime: ISO8601DateTime, $groupIds: [String!], $userMemberships: [MembershipInput!], $googleAnalyticsConnectorId: String, $keepDuplicatePages: Boolean, $bypassUrlRedirectCheck: Boolean) {
    createSite(accountId: $accountId, name: $name, url: $url, enabled: $enabled, a11yCheck: $a11yCheck, bestPracticesCheck: $bestPracticesCheck, spellCheck: $spellCheck, profanityCheck: $profanityCheck, linkCheck: $linkCheck, seoCheck: $seoCheck, webGovernanceCheck: $webGovernanceCheck, daysBetweenCrawls: $daysBetweenCrawls, customCheckIds: $customCheckIds, ignoredPaths: $ignoredPaths, ignoredPathRegexes: $ignoredPathRegexes, ignoredUrlRegexes: $ignoredUrlRegexes, dictionaries: $dictionaries, longWordsSplit: $longWordsSplit, ignoredAccessibility: $ignoredAccessibility, ignoredBestPractices: $ignoredBestPractices, ignoredLinks: $ignoredLinks, ignoredSeoIssues: $ignoredSeoIssues, ignoredWords: $ignoredWords, ignoreCapitalizedWords: $ignoreCapitalizedWords, ignoredProfaneWords: $ignoredProfaneWords, ignoredLinkReasons: $ignoredLinkReasons, crawlerConfiguration: $crawlerConfiguration, nextCrawlTime: $nextCrawlTime, groupIds: $groupIds, userMemberships: $userMemberships, googleAnalyticsConnectorId: $googleAnalyticsConnectorId, keepDuplicatePages: $keepDuplicatePages, bypassUrlRedirectCheck: $bypassUrlRedirectCheck) {
      site {
        ${SiteCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const UpdateSiteMutation = gql`
  mutation UpdateSite($accountId: String!, $siteId: String!, $name: String, $url: String, $enabled: Boolean, $a11yCheck: Boolean, $bestPracticesCheck: Boolean, $spellCheck: Boolean, $profanityCheck: Boolean, $linkCheck: Boolean, $seoCheck: Boolean, $webGovernanceCheck: Boolean, $daysBetweenCrawls: Int, $customCheckIds: [String!], $ignoredPaths: [String!], $ignoredPathRegexes: [String!], $ignoredUrlRegexes: [String!], $dictionaries: [SiteDictionary!], $longWordsSplit: Int, $ignoredAccessibility: [IgnoreIssuesInput!], $ignoredBestPractices: [IgnoreIssuesInput!], $ignoredLinkReasons: [String!], $ignoredLinks: [String!], $ignoredSeoIssues: [String!], $ignoredWords: [String!], $ignoreCapitalizedWords: Boolean, $ignoredProfaneWords: [String!], $crawlerConfiguration: CrawlerConfigurationInput, $nextCrawlTime: ISO8601DateTime, $groupIds: [String!], $userMemberships: [MembershipInput!], $googleAnalyticsConnectorId: String, $keepDuplicatePages: Boolean, $bypassUrlRedirectCheck: Boolean) {
    updateSite(accountId: $accountId, siteId: $siteId, name: $name, url: $url, enabled: $enabled, a11yCheck: $a11yCheck, bestPracticesCheck: $bestPracticesCheck, spellCheck: $spellCheck, profanityCheck: $profanityCheck, linkCheck: $linkCheck, seoCheck: $seoCheck, webGovernanceCheck: $webGovernanceCheck, daysBetweenCrawls: $daysBetweenCrawls, customCheckIds: $customCheckIds, ignoredPaths: $ignoredPaths, ignoredPathRegexes: $ignoredPathRegexes, ignoredUrlRegexes: $ignoredUrlRegexes, dictionaries: $dictionaries, longWordsSplit: $longWordsSplit, ignoredAccessibility: $ignoredAccessibility, ignoredBestPractices: $ignoredBestPractices, ignoredLinkReasons: $ignoredLinkReasons, ignoredLinks: $ignoredLinks, ignoredSeoIssues: $ignoredSeoIssues, ignoredWords: $ignoredWords, ignoreCapitalizedWords: $ignoreCapitalizedWords, ignoredProfaneWords: $ignoredProfaneWords, crawlerConfiguration: $crawlerConfiguration, nextCrawlTime: $nextCrawlTime, groupIds: $groupIds, userMemberships: $userMemberships, googleAnalyticsConnectorId: $googleAnalyticsConnectorId, keepDuplicatePages: $keepDuplicatePages, bypassUrlRedirectCheck: $bypassUrlRedirectCheck) {
      site {
        ${SiteCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const ToggleSiteMutation = gql`
  mutation ToggleSite($accountId: String!, $siteId: String!, $enabled: Boolean) {
    updateSite(accountId: $accountId, siteId: $siteId, enabled: $enabled) {
      site {
        ${SiteCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const ToggleUptimeMonitoringPausedMutation = gql`
  mutation ToggleUptimeMonitoring($accountId: String!, $siteId: String!, $uptimeMonitoring: UptimeMonitoringInput) {
    updateSite(accountId: $accountId, siteId: $siteId, uptimeMonitoring: $uptimeMonitoring) {
      site {
        ${SiteCoreAttributes}
        ${ErrorsAttributes}
        uptimeMonitoring {
          url
          paused
          lastResult {
            time
            status
            statusText
            isOk
          }
          lastStatusChange {
            time
            status
            statusText
            isOk
          }
        }
      }
    }
  }
`;

export const CrawlSiteMutation = gql`
  mutation CrawlSite($accountId: String!, $siteId: String!) {
    crawlSite(accountId: $accountId, siteId: $siteId) {
      site {
        ${SiteCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const CrawlSiteHardRefreshMutation = gql`
  mutation CrawlSiteHardRefresh($accountId: String!, $siteId: String!) {
    crawlSite(accountId: $accountId, siteId: $siteId, hardRefresh: true) {
      site {
        ${SiteCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const CancelCrawlSiteMutation = gql`
  mutation CancelCrawlSite($accountId: String!, $siteId: String!) {
    cancelCrawlSite(accountId: $accountId, siteId: $siteId) {
      site {
        ${SiteCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const ReanalyzeSiteMutation = gql`
  mutation ReanalyzeSite($accountId: String!, $siteId: String!) {
    reanalyzeSite(accountId: $accountId, siteId: $siteId) {
      site {
        ${SiteCoreAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const GenerateSiteSnapshotMutation = gql`
  mutation GenerateSnapshot($accountId: String!, $siteId: String!) {
    generateSiteSnapshot(accountId: $accountId, siteId: $siteId) {
      site {
        id
        latestStatsSnapshot {
          ${SiteDashboardSnapshotFields}
        }
      }
    }
  }
`;

export const DestroySitesMutation = gql`
  mutation DestroySites($accountId: String!, $siteIds: [String!]!) {
    destroySites(accountId: $accountId, siteIds: $siteIds) {
      status
    }
  }
`;

export const crawlerMethodTypes = [
  'hierarchical',
  'full_domain',
  'sitemap',
  'url_list',
  // Stopped being supported long ago, should it even be in the codebase anymore?
  //'top_google_analytics_page_views',
];

export const siteRoles = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

export const siteStateDefaults = {
  name: '',
  url: '',
  enabled: true,
  groups: [],
  daysBetweenCrawls: 7,
  keepDuplicatePages: false,
  crawlerConfiguration: {
    method: 'hierarchical',
    crawlDepth: 10,
    pageLimit: 0,
    obeyRobotsTxt: true,
    protocolAgnostic: false,
    onloadClickSelectors: '',
    removeMatchingElements: '',
    pagesCount: 100,
    urlList: [],
    googleAnalyticsConnectorId: '',
    timeout: 60,
    delay: '0',
    processingDelay: '0',
    ignoreUrlQueryPages: true,
    userAgentOverride: '',
    passwordProtected: false,
    protectedSiteType: 'basic',
    username: '',
    password: '',
    loginPageUrl: '',
    usernameFieldSelector: '',
    passwordFieldSelector: '',
    formSubmitSelector: '',
    authScriptSteps: [],
    pdfCheck: true,
    disableProxy: true,
    scrollPage: false,
    scrollPageSelector: '',
    scheduleCrawl: false,
    crawlDay: "sunday",
    crawlHour: 1,
    injectedJs: "",
    viewportHeight: null,
    viewportWidth: null,
    viewportIsLandscape: false,
    viewportDeviceScaleFactor: 1,
    viewportIsMobile: false,
    viewportHasTouch: false,
    additionalDomains: [],
    wwwAgnostic: false,
    waitUntilElementType: 'none',
    waitUntilElementSelector: null,
    reduceMotion: false,
  },
  dictionaries: ['blank'],
  bypassUrlRedirectCheck: false,
  customCheckIds: [],
  ignoredLinks: [],
  ignoredPaths: [],
  ignoredPathRegexes: [],
  ignoredUrlRegexes: [],
  ignoredProfaneWords: [],
  ignoredWords: [],
  ignoreCapitalizedWords: true,
  ignoredAccessibility: [],
  ignoredBestPractices: [],
  ignoredLinkReasons: [],
  ignoredSeoIssues: [],
  a11yCheck: true,
  bestPracticesCheck: true,
  linkCheck: true,
  profanityCheck: true,
  spellCheck: true,
  seoCheck: true,
  webGovernanceCheck: true,
  customChecks: undefined
};

export const crawlUrlObjectTypes = [
  'page',
  'asset',
];

export const crawlUrlStatusTypes = [
  'queued',
  'crawling',
  'processing',
  'crawled',
  'error',
  'skipped',
  'ignored'
];

export const crawlScheduleDays = [
  "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"
];

export const crawlerProtectedSiteTypes = [
  { value: 'basic', label: 'Basic' },
  { value: 'form', label: 'Form' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'script', label: 'Script' },
];

export const authScriptStepFillTokens = {
  username: '{{USERNAME}}',
  password: '{{PASSWORD}}',
};

export const getAuthScriptStepFillTarget = (args = []) => {
  const value = args[1];
  if (value === authScriptStepFillTokens.password) return 'password';
  if (value === authScriptStepFillTokens.username) return 'username';
  return 'custom';
};

export const authScriptStepDefaultArgs = {
  wait_for_millis: { args: ['5000'] },
  goto: { args: [''] },
  wait_for_selector: { args: [''] },
  fill: { args: ['', authScriptStepFillTokens.username] },
  click: { args: [''] },
  type: { args: [''] },
  //press: { args: [''] },
  wait_for_url: { args: [''] },
};

export const createAuthScriptStepFromAction = (action) => {
  const defaults = authScriptStepDefaultArgs[action] || { args: [''] };
  return {
    action,
    args: [...defaults.args],
    ...(defaults.namedArgs && { namedArgs: { ...defaults.namedArgs } }),
  };
};

export const waitUntilElementTypes = ['none', 'attached', 'detached', 'visible', 'hidden'];

export const getAuthScriptStepWaitForSelectorState = (step) => {
  const { state } = step?.namedArgs || {};
  return state && waitUntilElementTypes.includes(state) ? state : 'none';
};

const authScriptStepActionLabel = (key) => i18next.t(`model.site.crawler_configuration.${key}`);

export const authScriptStepActionTypes = [
  { value: 'wait_for_millis', label: authScriptStepActionLabel('auth_script_step_action_wait_for_millis') },
  { value: 'goto', label: authScriptStepActionLabel('auth_script_step_action_goto') },
  { value: 'wait_for_selector', label: authScriptStepActionLabel('auth_script_step_action_wait_for_selector') },
  { value: 'fill', label: authScriptStepActionLabel('auth_script_step_action_fill') },
  { value: 'type', label: authScriptStepActionLabel('auth_script_step_action_type') },
  //{ value: 'press', label: authScriptStepActionLabel('auth_script_step_action_press') },
  { value: 'wait_for_url', label: authScriptStepActionLabel('auth_script_step_action_wait_for_url') },
  { value: 'click', label: authScriptStepActionLabel('auth_script_step_action_click') },
].sort((a, b) => a.label.localeCompare(b.label));

export const sanitizeAdditionalDomains = (additionalDomains) => (additionalDomains || []).map(d => d.trim()).filter(d => d !== '');

const isValidAuthScriptStepAction = (action) => {
  return authScriptStepActionTypes.some(type => type.value === action);
};

const normalizeAuthScriptSteps = (authScriptSteps) => (
  authScriptSteps
    .filter(step => step && isValidAuthScriptStepAction(step.action))
    .map(step => ({
      action: step.action,
      args: (step.args || []).filter(arg => arg !== undefined && arg !== null && arg !== ''),
      namedArgs: step.namedArgs,
    }))
);

export const createSitePayload = (siteData, acl) => {
  const createPayload = {
    name: siteData.name,
    url: siteData.url,
    enabled: siteData.enabled,
    groupIds: siteData.groups.map(group => group.id),
    userMemberships: siteData.userMemberships,
    daysBetweenCrawls: siteData.daysBetweenCrawls * 1,
    keepDuplicatePages: siteData.keepDuplicatePages,
    crawlerConfiguration: {
      method: siteData.crawlerConfiguration.method,
      googleAnalyticsConnectorId: siteData.crawlerConfiguration.googleAnalyticsConnectorId,
      crawlDepth: siteData.crawlerConfiguration.crawlDepth * 1,
      pageLimit: siteData.crawlerConfiguration.pageLimit * 1,
      obeyRobotsTxt: siteData.crawlerConfiguration.obeyRobotsTxt,
      protocolAgnostic: siteData.crawlerConfiguration.protocolAgnostic,
      onloadClickSelectors: siteData.crawlerConfiguration.onloadClickSelectors,
      removeMatchingElements: siteData.crawlerConfiguration.removeMatchingElements,
      pagesCount: siteData.crawlerConfiguration.pagesCount * 1,
      urlList: siteData.crawlerConfiguration.urlList,
      timeout: siteData.crawlerConfiguration.timeout * 1,
      delay: siteData.crawlerConfiguration.delay * 1,
      processingDelay: siteData.crawlerConfiguration.processingDelay * 1,
      ignoreUrlQueryPages: siteData.crawlerConfiguration.ignoreUrlQueryPages,
      userAgentOverride: siteData.crawlerConfiguration.userAgentOverride,
      passwordProtected: siteData.crawlerConfiguration.passwordProtected,
      protectedSiteType: siteData.crawlerConfiguration.protectedSiteType,
      username: siteData.crawlerConfiguration.username,
      password: siteData.crawlerConfiguration.password,
      loginPageUrl: siteData.crawlerConfiguration.loginPageUrl,
      usernameFieldSelector: siteData.crawlerConfiguration.usernameFieldSelector,
      passwordFieldSelector: siteData.crawlerConfiguration.passwordFieldSelector,
      formSubmitSelector: siteData.crawlerConfiguration.formSubmitSelector,
      authScriptSteps: normalizeAuthScriptSteps(siteData.crawlerConfiguration.authScriptSteps),
      pdfCheck: siteData.crawlerConfiguration.pdfCheck,
      disableProxy: siteData.crawlerConfiguration.disableProxy,
      scrollPage: siteData.crawlerConfiguration.scrollPage,
      scrollPageSelector: siteData.crawlerConfiguration.scrollPageSelector,
      scheduleCrawl: siteData.crawlerConfiguration.scheduleCrawl,
      crawlDay: siteData.crawlerConfiguration.crawlDay,
      crawlHour: siteData.crawlerConfiguration.crawlHour,
      injectedJs: siteData.crawlerConfiguration.injectedJs,
      viewportHeight: siteData.crawlerConfiguration.viewportHeight ? siteData.crawlerConfiguration.viewportHeight * 1 : null,
      viewportWidth: siteData.crawlerConfiguration.viewportWidth ? siteData.crawlerConfiguration.viewportWidth * 1 : null,
      viewportDeviceScaleFactor: siteData.crawlerConfiguration.viewportDeviceScaleFactor ? siteData.crawlerConfiguration.viewportDeviceScaleFactor * 1 : null,
      viewportIsMobile: siteData.crawlerConfiguration.viewportIsMobile,
      viewportHasTouch: siteData.crawlerConfiguration.viewportHasTouch,
      viewportIsLandscape: siteData.crawlerConfiguration.viewportIsLandscape,
      additionalDomains: sanitizeAdditionalDomains(siteData.crawlerConfiguration.additionalDomains),
      wwwAgnostic: siteData.crawlerConfiguration.wwwAgnostic,
      waitUntilElementType: siteData.crawlerConfiguration.waitUntilElementType,
      waitUntilElementSelector: siteData.crawlerConfiguration.waitUntilElementType === 'none' ? null : siteData.crawlerConfiguration.waitUntilElementSelector,
      reduceMotion: siteData.crawlerConfiguration.reduceMotion,
    },
    longWordsSplit: siteData.longWordsSplit ? siteData.longWordsSplit * 1 : null,
    dictionaries: siteData.dictionaries,
    bypassUrlRedirectCheck: siteData.bypassUrlRedirectCheck,
    ignoredLinks: siteData.ignoredLinks,
    ignoredPaths: siteData.ignoredPaths,
    ignoredPathRegexes: siteData.ignoredPathRegexes,
    ignoredUrlRegexes: siteData.ignoredUrlRegexes,
    ignoredProfaneWords: siteData.ignoredProfaneWords,
    ignoredWords: siteData.ignoredWords,
    ignoreCapitalizedWords: siteData.ignoreCapitalizedWords,
    ignoredBestPractices: siteData.ignoredBestPractices.map(issue => ({ id: issue.issueId || issue.id, help: issue.help, elementSource: issue.elementSource })),
    ignoredAccessibility: siteData.ignoredAccessibility.map(issue => ({ id: issue.issueId || issue.id, help: issue.help, elementSource: issue.elementSource })),
    ignoredLinkReasons: siteData.ignoredLinkReasons,
    a11yCheck: siteData.a11yCheck,
    bestPracticesCheck: siteData.bestPracticesCheck,
    linkCheck: siteData.linkCheck,
    profanityCheck: siteData.profanityCheck,
    seoCheck: siteData.seoCheck,
    spellCheck: siteData.spellCheck,
    webGovernanceCheck: siteData.webGovernanceCheck,
    customCheckIds: [],
    googleAnalyticsConnectorId: siteData.googleAnalyticsConnectorId,
  };

  siteData.customChecks.forEach(c => {
    createPayload.customCheckIds.push(c.id);
  });

  return createPayload;
}

export const editSitePayload = (siteData, acl) => {
  let updatePayload = {
    name: siteData.name,
    url: siteData.url,
    enabled: siteData.enabled,
    groupIds: siteData.groups.map(group => group.id),
    userMemberships: siteData.userMemberships,
    daysBetweenCrawls: siteData.daysBetweenCrawls * 1,
    keepDuplicatePages: siteData.keepDuplicatePages,
    crawlerConfiguration: {
      method: siteData.crawlerConfiguration.method,
      googleAnalyticsConnectorId: siteData.crawlerConfiguration.googleAnalyticsConnectorId,
      crawlDepth: siteData.crawlerConfiguration.crawlDepth * 1,
      pageLimit: siteData.crawlerConfiguration.pageLimit * 1,
      obeyRobotsTxt: siteData.crawlerConfiguration.obeyRobotsTxt,
      protocolAgnostic: siteData.crawlerConfiguration.protocolAgnostic,
      onloadClickSelectors: siteData.crawlerConfiguration.onloadClickSelectors,
      removeMatchingElements: siteData.crawlerConfiguration.removeMatchingElements,
      pagesCount: siteData.crawlerConfiguration.pagesCount * 1,
      urlList: siteData.crawlerConfiguration.urlList,
      timeout: siteData.crawlerConfiguration.timeout * 1,
      delay: siteData.crawlerConfiguration.delay * 1,
      processingDelay: siteData.crawlerConfiguration.processingDelay * 1,
      ignoreUrlQueryPages: siteData.crawlerConfiguration.ignoreUrlQueryPages,
      userAgentOverride: siteData.crawlerConfiguration.userAgentOverride,
      passwordProtected: siteData.crawlerConfiguration.passwordProtected,
      protectedSiteType: siteData.crawlerConfiguration.protectedSiteType,
      username: siteData.crawlerConfiguration.username,
      password: siteData.crawlerConfiguration.password,
      loginPageUrl: siteData.crawlerConfiguration.loginPageUrl,
      usernameFieldSelector: siteData.crawlerConfiguration.usernameFieldSelector,
      passwordFieldSelector: siteData.crawlerConfiguration.passwordFieldSelector,
      formSubmitSelector: siteData.crawlerConfiguration.formSubmitSelector,
      authScriptSteps: normalizeAuthScriptSteps(siteData.crawlerConfiguration.authScriptSteps),
      pdfCheck: siteData.crawlerConfiguration.pdfCheck,
      disableProxy: siteData.crawlerConfiguration.disableProxy,
      scrollPage: siteData.crawlerConfiguration.scrollPage,
      scrollPageSelector: siteData.crawlerConfiguration.scrollPageSelector,
      scheduleCrawl: siteData.crawlerConfiguration.scheduleCrawl,
      crawlDay: siteData.crawlerConfiguration.crawlDay,
      crawlHour: siteData.crawlerConfiguration.crawlHour,
      injectedJs: siteData.crawlerConfiguration.injectedJs,
      viewportHeight: siteData.crawlerConfiguration.viewportHeight ? siteData.crawlerConfiguration.viewportHeight * 1 : null,
      viewportWidth: siteData.crawlerConfiguration.viewportWidth ? siteData.crawlerConfiguration.viewportWidth * 1 : null,
      viewportDeviceScaleFactor: siteData.crawlerConfiguration.viewportDeviceScaleFactor ? siteData.crawlerConfiguration.viewportDeviceScaleFactor * 1 : null,
      viewportIsMobile: siteData.crawlerConfiguration.viewportIsMobile,
      viewportHasTouch: siteData.crawlerConfiguration.viewportHasTouch,
      viewportIsLandscape: siteData.crawlerConfiguration.viewportIsLandscape,
      additionalDomains: sanitizeAdditionalDomains(siteData.crawlerConfiguration.additionalDomains),
      wwwAgnostic: siteData.crawlerConfiguration.wwwAgnostic,
      waitUntilElementType: siteData.crawlerConfiguration.waitUntilElementType,
      waitUntilElementSelector: siteData.crawlerConfiguration.waitUntilElementType === 'none' ? null : siteData.crawlerConfiguration.waitUntilElementSelector,
      reduceMotion: siteData.crawlerConfiguration.reduceMotion,
    },
    longWordsSplit: siteData.longWordsSplit ? siteData.longWordsSplit * 1 : null,
    dictionaries: siteData.dictionaries,
    bypassUrlRedirectCheck: siteData.bypassUrlRedirectCheck,
    ignoredPaths: siteData.ignoredPaths,
    ignoredPathRegexes: siteData.ignoredPathRegexes,
    ignoredUrlRegexes: siteData.ignoredUrlRegexes,
    a11yCheck: siteData.a11yCheck,
    bestPracticesCheck: siteData.bestPracticesCheck,
    linkCheck: siteData.linkCheck,
    profanityCheck: siteData.profanityCheck,
    seoCheck: siteData.seoCheck,
    spellCheck: siteData.spellCheck,
    webGovernanceCheck: siteData.webGovernanceCheck,
    ignoredBestPractices: siteData.ignoredBestPractices.map(issue => ({ id: issue.issueId || issue.id, help: issue.help, elementSource: issue.elementSource })),
    ignoredAccessibility: siteData.ignoredAccessibility.map(issue => ({ id: issue.issueId || issue.id, help: issue.help, elementSource: issue.elementSource })),
    ignoredLinks: siteData.ignoredLinks,
    ignoredLinkReasons: siteData.ignoredLinkReasons,
    ignoredProfaneWords: siteData.ignoredProfaneWords,
    ignoredSeoIssues: siteData.ignoredSeoIssues.map(issue => issue.id),
    ignoredWords: siteData.ignoredWords,
    ignoreCapitalizedWords: siteData.ignoreCapitalizedWords,
    customCheckIds: siteData.customChecks.map(c => c.id),
    googleAnalyticsConnectorId: siteData.googleAnalyticsConnectorId,
  };

  return updatePayload;
}

export function requestCrawl({ mutation, accountId, siteId, onSuccess }) {
  if (!window.confirm(i18next.t("screens.sites.prompt_crawl"))) {
    return false;
  }

  mutation({ variables: { accountId, siteId } })
    .then(r => {
      if (r.data.crawlSite.site.errorMessages && r.data.crawlSite.site.errorMessages.length) {
        Snackbar.error(r.data.crawlSite.site.errorMessages.join('. '));
      } else {
        Snackbar.success(i18next.t("screens.sites.recrawling_site", { site: r.data.crawlSite.site }));
        onSuccess && onSuccess();
      }
    })
    .catch(e => {
      const errorMessage = e.graphQLErrors && e.graphQLErrors.length === 1 && e.graphQLErrors[0].message
      if (errorMessage === "robots_txt_blocking_url") {
        Snackbar.error(i18next.t("screens.sites.robots_txt_blocking_url"));
      } else if (errorMessage === "crawls_stopped") {
        Snackbar.error(i18next.t("screens.sites.crawls_stopped"));
      } else {
        Snackbar.error(i18next.t("screens.sites.error_recrawling"));
      }
    });
}

export function requestCancelCrawl({ mutation, accountId, siteId, siteName, onSuccess }) {
  if (!window.confirm(i18next.t("screens.sites.prompt_cancel_crawl", { siteName }))) {
    return false;
  }

  mutation({ variables: { accountId, siteId } })
    .then(r => {
      if (r.data.cancelCrawlSite.site.errorMessages && r.data.cancelCrawlSite.site.errorMessages.length) {
        Snackbar.error(r.data.cancelCrawlSite.site.errorMessages.join('. '));
      } else {
        Snackbar.success(i18next.t("screens.sites.cancelled_crawl", { siteName }));
        onSuccess && onSuccess();
      }
    })
    .catch(_ => Snackbar.error(i18next.t("screens.sites.error_cancelling_crawl", { siteName })));
};


export const renderSiteUrlForTableCell = (value, crawlMethod, skipTruncate = false) => {
  if (['hierarchical', 'full_domain'].includes(crawlMethod)) {
    return skipTruncate ? value : StringUtil.truncate(value, 75);
  }

  const crawlerMethodType = crawlerMethodTypes.find(type => type === crawlMethod);
  return crawlerMethodType ? i18next.t(`model.site.crawler_method_types.${crawlerMethodType}`) : i18next.t("common.na");
};

export const isSiteProcessing = (state) => {
  return typeof state === "string" && ["queued", "crawling"].includes(state);
};
